'use client';

import type { ComponentPropsWithoutRef, ForwardedRef } from 'react';

import type { DataAttributesProps } from '../../../utils/types';
import type { SelectOption } from '../common';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { forwardRef, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../ui/command';
import { Icon } from '../../icons';

/**
 * Single-select dropdown with a built-in search input. Drop-in replacement for
 * `Select` when the option list is long enough that filtering helps.
 */
type SearchableSelectProps = DataAttributesProps &
  Omit<
    ComponentPropsWithoutRef<'button'>,
    'onChange' | 'role' | 'type' | 'value'
  > & {
    emptyLabel?: string;
    name?: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    placeholder?: string;
    searchPlaceholder?: string;
    value?: string;
  };

export const SearchableSelect = forwardRef(
  (
    {
      className,
      'data-testid': dataTestId,
      disabled,
      emptyLabel = "Pas d'éléments trouvés",
      name,
      onChange,
      options,
      placeholder = 'Sélectionner',
      searchPlaceholder = 'Rechercher',
      value,
      ...rest
    }: SearchableSelectProps,
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    const testId = dataTestId ?? name;
    const [open, setOpen] = useState(false);

    const selectedOption = options.find((option) => option.value === value);

    const handleSelect = (selected: SelectOption) => {
      onChange(selected.value);
      setOpen(false);
    };

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={ref}
            {...rest}
            aria-expanded={open}
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
              className
            )}
            data-testid={testId}
            disabled={disabled}
            role="combobox"
            type="button"
          >
            <span
              className={cn(
                'truncate text-left',
                !selectedOption && 'text-muted-foreground'
              )}
            >
              {selectedOption?.label ?? placeholder}
            </span>
            <Icon className="ml-2 shrink-0" name="chevrons-up-down" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          <Command className="w-full">
            <CommandInput className="h-10" placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyLabel}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    className="flex items-center justify-between gap-2"
                    data-testid={`${testId}-${option.value}`}
                    value={option.label}
                    onSelect={() => handleSelect(option)}
                  >
                    <span className="truncate">{option.label}</span>
                    {option.value === value && <Icon name="check" size="xs" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

SearchableSelect.displayName = 'SearchableSelect';
